import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, extractApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Capture token from /auth/callback?token=...
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get("token");
    if (tokenParam) {
      localStorage.setItem("cardlon_token", tokenParam);
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cardlon_token", data.access_token);
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const register = async (email, username, password, refCode, accountType, extra = {}) => {
    try {
      const body = { email, username, password };
      if (refCode) body.ref_code = refCode;
      if (accountType) body.account_type = accountType;
      if (extra.first_name) body.first_name = extra.first_name;
      if (extra.last_name) body.last_name = extra.last_name;
      if (extra.dob) body.dob = extra.dob;
      // v17: Promo-Code direkt mitschicken, damit er auch ohne Email-Bestätigung gilt
      if (extra.promo_code) body.promo_code = extra.promo_code;
      const { data } = await api.post("/auth/register", body);
      // Strict email-verification flow: NO auto-login. Caller must redirect to pending page.
      if (data?.email_verification_required) {
        return { ok: true, requiresVerification: true, email, promo: data?.promo, referral: data?.referral };
      }
      // Legacy fallback (shouldn't happen now)
      if (data?.access_token) {
        localStorage.setItem("cardlon_token", data.access_token);
        await fetchMe();
      }
      return { ok: true, referral: data?.referral, promo: data?.promo };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const resendVerification = async (email) => {
    try {
      await api.post("/auth/resend-verification", { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      return { ok: true, message: data?.message };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("cardlon_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, resendVerification, verifyEmail }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
